// Shared geography resolution for the Stage 3 maps.
//
// The location labels coming from the hierarchy / selection lists are messy:
// "Bangalore New CFA", "Bhiwandi.CFA (PH+AH)", "Delhi CFA", "Karnataka",
// "South", "TL" ... The old maps only matched a handful of hardcoded names, so
// most selections never highlighted anything. resolveLocation() takes any of
// those labels and returns the state(s) to highlight plus a point to zoom to.

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const COUNTRIES = {
  india: { lat: 22, lon: 79 },
  bangladesh: { lat: 23.7, lon: 90.3 },
  germany: { lat: 51, lon: 10 },
  "sri lanka": { lat: 7.9, lon: 80.8 },
  nepal: { lat: 28.2, lon: 84 },
  bhutan: { lat: 27.4, lon: 90.4 },
  pakistan: { lat: 30, lon: 70 },
};

const REGIONS = {
  north: ["Jammu and Kashmir", "Ladakh", "Himachal Pradesh", "Punjab", "Uttarakhand", "Haryana", "Delhi", "Chandigarh", "Uttar Pradesh", "Rajasthan"],
  south: ["Andhra Pradesh", "Telangana", "Karnataka", "Kerala", "Tamil Nadu", "Puducherry"],
  east: ["Bihar", "Jharkhand", "Odisha", "West Bengal"],
  west: ["Rajasthan", "Gujarat", "Maharashtra", "Goa"],
  central: ["Madhya Pradesh", "Chhattisgarh"],
  "north east": ["Assam", "Arunachal Pradesh", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Tripura", "Sikkim"],
};
REGIONS.northeast = REGIONS["north east"];

const REGION_POINTS = {
  north: { lat: 28.5, lon: 77 },
  south: { lat: 13, lon: 78 },
  east: { lat: 23, lon: 86 },
  west: { lat: 20, lon: 73 },
  central: { lat: 23, lon: 79 },
  "north east": { lat: 26, lon: 93 },
  northeast: { lat: 26, lon: 93 },
};

// Canonical state name -> centre point.
const STATES = {
  "andhra pradesh": { lat: 15.9, lon: 79.7 },
  "arunachal pradesh": { lat: 28, lon: 94.7 },
  assam: { lat: 26.2, lon: 92.9 },
  bihar: { lat: 25.9, lon: 85.6 },
  chandigarh: { lat: 30.73, lon: 76.78 },
  chhattisgarh: { lat: 21.3, lon: 82 },
  delhi: { lat: 28.6, lon: 77.1 },
  goa: { lat: 15.4, lon: 74 },
  gujarat: { lat: 22.7, lon: 71.5 },
  haryana: { lat: 29.2, lon: 76.3 },
  "himachal pradesh": { lat: 31.8, lon: 77.3 },
  "jammu and kashmir": { lat: 33.8, lon: 76.6 },
  jharkhand: { lat: 23.6, lon: 85.3 },
  karnataka: { lat: 15.3, lon: 75.7 },
  kerala: { lat: 10.5, lon: 76.3 },
  ladakh: { lat: 34.2, lon: 77.6 },
  "madhya pradesh": { lat: 23.5, lon: 78.5 },
  maharashtra: { lat: 19.5, lon: 76 },
  manipur: { lat: 24.7, lon: 93.9 },
  meghalaya: { lat: 25.5, lon: 91.4 },
  mizoram: { lat: 23.2, lon: 92.9 },
  nagaland: { lat: 26.1, lon: 94.5 },
  odisha: { lat: 20.5, lon: 84.5 },
  puducherry: { lat: 11.9, lon: 79.8 },
  punjab: { lat: 31, lon: 75.4 },
  rajasthan: { lat: 26.6, lon: 73.8 },
  sikkim: { lat: 27.6, lon: 88.5 },
  "tamil nadu": { lat: 11.1, lon: 78.7 },
  telangana: { lat: 17.9, lon: 79.6 },
  tripura: { lat: 23.8, lon: 91.7 },
  "uttar pradesh": { lat: 27, lon: 80.9 },
  uttarakhand: { lat: 30.1, lon: 79.3 },
  "west bengal": { lat: 23.5, lon: 87.8 },
  "andaman and nicobar": { lat: 11.7, lon: 92.7 },
};

// Name variants / codes -> canonical state key.
const STATE_ALIASES = {
  orissa: "odisha",
  pondicherry: "puducherry",
  uttaranchal: "uttarakhand",
  "nct of delhi": "delhi",
  "national capital territory of delhi": "delhi",
  "jammu & kashmir": "jammu and kashmir",
  "jammu kashmir": "jammu and kashmir",
  "andaman and nicobar islands": "andaman and nicobar",
  "andaman nicobar": "andaman and nicobar",
};

// Whole-label short codes (only matched when the label is exactly the code).
const SHORT_CODES = {
  ap: "andhra pradesh", tl: "telangana", ka: "karnataka", kl: "kerala",
  tn: "tamil nadu", mh: "maharashtra", gj: "gujarat", rj: "rajasthan",
  up: "uttar pradesh", uk: "uttarakhand", mp: "madhya pradesh", cg: "chhattisgarh",
  wb: "west bengal", jh: "jharkhand", br: "bihar", od: "odisha", or: "odisha",
  hr: "haryana", pb: "punjab", hp: "himachal pradesh", ga: "goa",
  jk: "jammu and kashmir", as: "assam", dl: "delhi",
};

// Canonical city name -> { state, lat, lon }.
const CITIES = {
  // Maharashtra
  mumbai: { state: "maharashtra", lat: 19.08, lon: 72.88 },
  "navi mumbai": { state: "maharashtra", lat: 19.03, lon: 73.03 },
  pune: { state: "maharashtra", lat: 18.52, lon: 73.86 },
  nagpur: { state: "maharashtra", lat: 21.15, lon: 79.09 },
  nashik: { state: "maharashtra", lat: 20.0, lon: 73.79 },
  aurangabad: { state: "maharashtra", lat: 19.88, lon: 75.34 },
  bhiwandi: { state: "maharashtra", lat: 19.3, lon: 73.06 },
  thane: { state: "maharashtra", lat: 19.22, lon: 72.98 },
  kalyan: { state: "maharashtra", lat: 19.24, lon: 73.13 },
  solapur: { state: "maharashtra", lat: 17.66, lon: 75.91 },
  kolhapur: { state: "maharashtra", lat: 16.7, lon: 74.24 },
  amravati: { state: "maharashtra", lat: 20.93, lon: 77.78 },
  nanded: { state: "maharashtra", lat: 19.16, lon: 77.31 },
  sangli: { state: "maharashtra", lat: 16.85, lon: 74.58 },

  // Delhi NCR
  delhi: { state: "delhi", lat: 28.65, lon: 77.19 },
  "new delhi": { state: "delhi", lat: 28.61, lon: 77.21 },
  noida: { state: "uttar pradesh", lat: 28.54, lon: 77.39 },
  "greater noida": { state: "uttar pradesh", lat: 28.47, lon: 77.5 },
  ghaziabad: { state: "uttar pradesh", lat: 28.67, lon: 77.45 },
  gurgaon: { state: "haryana", lat: 28.46, lon: 77.03 },
  faridabad: { state: "haryana", lat: 28.41, lon: 77.31 },

  // Uttar Pradesh
  lucknow: { state: "uttar pradesh", lat: 26.85, lon: 80.95 },
  kanpur: { state: "uttar pradesh", lat: 26.45, lon: 80.33 },
  agra: { state: "uttar pradesh", lat: 27.18, lon: 78.01 },
  varanasi: { state: "uttar pradesh", lat: 25.32, lon: 82.97 },
  meerut: { state: "uttar pradesh", lat: 28.98, lon: 77.71 },
  allahabad: { state: "uttar pradesh", lat: 25.44, lon: 81.85 },
  bareilly: { state: "uttar pradesh", lat: 28.37, lon: 79.43 },
  aligarh: { state: "uttar pradesh", lat: 27.88, lon: 78.08 },
  moradabad: { state: "uttar pradesh", lat: 28.84, lon: 78.77 },
  gorakhpur: { state: "uttar pradesh", lat: 26.76, lon: 83.37 },
  jhansi: { state: "uttar pradesh", lat: 25.45, lon: 78.57 },

  // Karnataka
  bangalore: { state: "karnataka", lat: 12.97, lon: 77.59 },
  mysore: { state: "karnataka", lat: 12.3, lon: 76.64 },
  hubli: { state: "karnataka", lat: 15.36, lon: 75.12 },
  mangalore: { state: "karnataka", lat: 12.91, lon: 74.86 },
  belgaum: { state: "karnataka", lat: 15.85, lon: 74.5 },
  gulbarga: { state: "karnataka", lat: 17.33, lon: 76.83 },
  davangere: { state: "karnataka", lat: 14.47, lon: 75.92 },

  // Telangana
  hyderabad: { state: "telangana", lat: 17.39, lon: 78.49 },
  warangal: { state: "telangana", lat: 17.97, lon: 79.59 },
  nizamabad: { state: "telangana", lat: 18.67, lon: 78.09 },
  karimnagar: { state: "telangana", lat: 18.44, lon: 79.13 },

  // Andhra Pradesh
  visakhapatnam: { state: "andhra pradesh", lat: 17.69, lon: 83.22 },
  vijayawada: { state: "andhra pradesh", lat: 16.51, lon: 80.65 },
  guntur: { state: "andhra pradesh", lat: 16.31, lon: 80.44 },
  nellore: { state: "andhra pradesh", lat: 14.44, lon: 79.99 },
  tirupati: { state: "andhra pradesh", lat: 13.63, lon: 79.42 },
  kurnool: { state: "andhra pradesh", lat: 15.83, lon: 78.04 },
  rajahmundry: { state: "andhra pradesh", lat: 17.0, lon: 81.8 },
  kakinada: { state: "andhra pradesh", lat: 16.99, lon: 82.25 },

  // Tamil Nadu
  chennai: { state: "tamil nadu", lat: 13.08, lon: 80.27 },
  coimbatore: { state: "tamil nadu", lat: 11.02, lon: 76.96 },
  madurai: { state: "tamil nadu", lat: 9.93, lon: 78.12 },
  tiruchirappalli: { state: "tamil nadu", lat: 10.79, lon: 78.7 },
  salem: { state: "tamil nadu", lat: 11.66, lon: 78.15 },
  tirunelveli: { state: "tamil nadu", lat: 8.71, lon: 77.76 },
  erode: { state: "tamil nadu", lat: 11.34, lon: 77.72 },
  vellore: { state: "tamil nadu", lat: 12.92, lon: 79.13 },
  thoothukudi: { state: "tamil nadu", lat: 8.76, lon: 78.13 },
  hosur: { state: "tamil nadu", lat: 12.74, lon: 77.83 },

  // Kerala
  kochi: { state: "kerala", lat: 9.93, lon: 76.27 },
  thiruvananthapuram: { state: "kerala", lat: 8.52, lon: 76.94 },
  kozhikode: { state: "kerala", lat: 11.26, lon: 75.78 },
  thrissur: { state: "kerala", lat: 10.53, lon: 76.21 },
  kollam: { state: "kerala", lat: 8.89, lon: 76.61 },
  kannur: { state: "kerala", lat: 11.87, lon: 75.37 },
  kottayam: { state: "kerala", lat: 9.59, lon: 76.52 },

  // West Bengal
  kolkata: { state: "west bengal", lat: 22.57, lon: 88.36 },
  howrah: { state: "west bengal", lat: 22.59, lon: 88.31 },
  durgapur: { state: "west bengal", lat: 23.52, lon: 87.31 },
  asansol: { state: "west bengal", lat: 23.68, lon: 86.98 },
  siliguri: { state: "west bengal", lat: 26.73, lon: 88.4 },
  kharagpur: { state: "west bengal", lat: 22.35, lon: 87.32 },

  // Odisha
  bhubaneswar: { state: "odisha", lat: 20.3, lon: 85.82 },
  cuttack: { state: "odisha", lat: 20.46, lon: 85.88 },
  rourkela: { state: "odisha", lat: 22.26, lon: 84.85 },
  sambalpur: { state: "odisha", lat: 21.47, lon: 83.97 },
  puri: { state: "odisha", lat: 19.81, lon: 85.83 },
  berhampur: { state: "odisha", lat: 19.31, lon: 84.79 },

  // Bihar / Jharkhand
  patna: { state: "bihar", lat: 25.59, lon: 85.14 },
  gaya: { state: "bihar", lat: 24.8, lon: 85.0 },
  muzaffarpur: { state: "bihar", lat: 26.12, lon: 85.39 },
  bhagalpur: { state: "bihar", lat: 25.24, lon: 86.99 },
  ranchi: { state: "jharkhand", lat: 23.36, lon: 85.33 },
  jamshedpur: { state: "jharkhand", lat: 22.8, lon: 86.2 },
  dhanbad: { state: "jharkhand", lat: 23.8, lon: 86.43 },
  bokaro: { state: "jharkhand", lat: 23.64, lon: 86.16 },

  // Gujarat
  ahmedabad: { state: "gujarat", lat: 23.03, lon: 72.58 },
  surat: { state: "gujarat", lat: 21.17, lon: 72.83 },
  vadodara: { state: "gujarat", lat: 22.31, lon: 73.18 },
  rajkot: { state: "gujarat", lat: 22.3, lon: 70.8 },
  bhavnagar: { state: "gujarat", lat: 21.76, lon: 72.15 },
  jamnagar: { state: "gujarat", lat: 22.47, lon: 70.06 },
  gandhinagar: { state: "gujarat", lat: 23.22, lon: 72.65 },

  // Rajasthan
  jaipur: { state: "rajasthan", lat: 26.91, lon: 75.79 },
  jodhpur: { state: "rajasthan", lat: 26.24, lon: 73.02 },
  udaipur: { state: "rajasthan", lat: 24.57, lon: 73.69 },
  kota: { state: "rajasthan", lat: 25.21, lon: 75.86 },
  ajmer: { state: "rajasthan", lat: 26.45, lon: 74.64 },
  bikaner: { state: "rajasthan", lat: 28.02, lon: 73.31 },
  alwar: { state: "rajasthan", lat: 27.55, lon: 76.63 },

  // Madhya Pradesh / Chhattisgarh
  bhopal: { state: "madhya pradesh", lat: 23.26, lon: 77.41 },
  indore: { state: "madhya pradesh", lat: 22.72, lon: 75.86 },
  jabalpur: { state: "madhya pradesh", lat: 23.18, lon: 79.99 },
  gwalior: { state: "madhya pradesh", lat: 26.22, lon: 78.18 },
  ujjain: { state: "madhya pradesh", lat: 23.18, lon: 75.78 },
  raipur: { state: "chhattisgarh", lat: 21.25, lon: 81.63 },
  bhilai: { state: "chhattisgarh", lat: 21.19, lon: 81.38 },
  bilaspur: { state: "chhattisgarh", lat: 22.08, lon: 82.15 },

  // Punjab / Haryana / Chandigarh
  chandigarh: { state: "chandigarh", lat: 30.73, lon: 76.78 },
  ludhiana: { state: "punjab", lat: 30.9, lon: 75.86 },
  amritsar: { state: "punjab", lat: 31.63, lon: 74.87 },
  jalandhar: { state: "punjab", lat: 31.33, lon: 75.58 },
  patiala: { state: "punjab", lat: 30.34, lon: 76.39 },
  bathinda: { state: "punjab", lat: 30.21, lon: 74.94 },
  panipat: { state: "haryana", lat: 29.39, lon: 76.97 },
  karnal: { state: "haryana", lat: 29.69, lon: 76.99 },
  hisar: { state: "haryana", lat: 29.15, lon: 75.72 },
  rohtak: { state: "haryana", lat: 28.9, lon: 76.61 },
  ambala: { state: "haryana", lat: 30.38, lon: 76.78 },
  panchkula: { state: "haryana", lat: 30.69, lon: 76.86 },

  // Uttarakhand / HP / J&K
  dehradun: { state: "uttarakhand", lat: 30.32, lon: 78.03 },
  haridwar: { state: "uttarakhand", lat: 29.95, lon: 78.16 },
  roorkee: { state: "uttarakhand", lat: 29.87, lon: 77.89 },
  haldwani: { state: "uttarakhand", lat: 29.22, lon: 79.51 },
  shimla: { state: "himachal pradesh", lat: 31.1, lon: 77.17 },
  "baddi": { state: "himachal pradesh", lat: 30.96, lon: 76.79 },
  jammu: { state: "jammu and kashmir", lat: 32.73, lon: 74.87 },
  srinagar: { state: "jammu and kashmir", lat: 34.08, lon: 74.8 },
  leh: { state: "ladakh", lat: 34.16, lon: 77.58 },

  // North East
  guwahati: { state: "assam", lat: 26.14, lon: 91.74 },
  dibrugarh: { state: "assam", lat: 27.47, lon: 94.91 },
  silchar: { state: "assam", lat: 24.83, lon: 92.78 },
  jorhat: { state: "assam", lat: 26.75, lon: 94.22 },
  imphal: { state: "manipur", lat: 24.82, lon: 93.95 },
  shillong: { state: "meghalaya", lat: 25.58, lon: 91.89 },
  aizawl: { state: "mizoram", lat: 23.73, lon: 92.72 },
  kohima: { state: "nagaland", lat: 25.67, lon: 94.11 },
  dimapur: { state: "nagaland", lat: 25.91, lon: 93.72 },
  agartala: { state: "tripura", lat: 23.83, lon: 91.28 },
  itanagar: { state: "arunachal pradesh", lat: 27.1, lon: 93.62 },
  gangtok: { state: "sikkim", lat: 27.33, lon: 88.61 },

  // Goa / Puducherry / Islands
  panaji: { state: "goa", lat: 15.49, lon: 73.83 },
  margao: { state: "goa", lat: 15.28, lon: 73.96 },
  vasco: { state: "goa", lat: 15.4, lon: 73.81 },
  puducherry: { state: "puducherry", lat: 11.94, lon: 79.83 },
  "port blair": { state: "andaman and nicobar", lat: 11.62, lon: 92.73 },
};

// Common alternate spellings -> canonical city key.
const CITY_ALIASES = {
  bengaluru: "bangalore",
  bangaluru: "bangalore",
  bombay: "mumbai",
  calcutta: "kolkata",
  kolkatta: "kolkata",
  madras: "chennai",
  gurugram: "gurgaon",
  mysuru: "mysore",
  mangaluru: "mangalore",
  hubballi: "hubli",
  belagavi: "belgaum",
  kalaburagi: "gulbarga",
  vizag: "visakhapatnam",
  vizagapatam: "visakhapatnam",
  trivandrum: "thiruvananthapuram",
  calicut: "kozhikode",
  trichy: "tiruchirappalli",
  trichinopoly: "tiruchirappalli",
  cochin: "kochi",
  ernakulam: "kochi",
  pondicherry: "puducherry",
  pondy: "puducherry",
  prayagraj: "allahabad",
  baroda: "vadodara",
  "bhubaneshwar": "bhubaneswar",
  bhubneswar: "bhubaneswar",
  gauhati: "guwahati",
  "vasco da gama": "vasco",
  "navi mumbai": "navi mumbai",
};

// Generic words to drop from a label before token matching.
const STOPWORDS = new Set([
  "cfa", "cfas", "cf", "cnf", "c", "fa", "depot", "warehouse", "whse", "wh",
  "dc", "ho", "rso", "rs", "godown", "hub", "plant", "factory", "unit",
  "branch", "office", "stockpoint", "stock", "point", "superstockist",
  "distributor", "dealer", "new", "old", "main", "ltd", "pvt", "india",
  "ph", "ah", "cfs", "icd", "yard", "region", "zone", "area", "the",
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function normalize(str) {
  return String(str || "")
    .toLowerCase()
    .replace(/\(.*?\)/g, " ") // drop "(PH+AH)" etc.
    .replace(/[^a-z0-9]+/g, " ") // punctuation -> space
    .replace(/\s+/g, " ")
    .trim();
}

// Does an amCharts polygon name refer to the given state? Accepts the canonical
// key or a title-cased variant on either side.
export function stateMatchesPolygon(polygonName, canonical) {
  let p = normalize(polygonName);
  let c = normalize(canonical);
  if (!p || !c) return false;
  if (STATE_ALIASES[p]) p = STATE_ALIASES[p];
  if (STATE_ALIASES[c]) c = STATE_ALIASES[c];
  if (p === c) return true;
  // whole-word containment ("nct of delhi" -> "delhi")
  const re = new RegExp("(^| )" + c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "( |$)");
  return c.length > 3 && re.test(p);
}

function lookupExact(key) {
  if (!key) return null;
  if (COUNTRIES[key]) return { kind: "country", label: key, states: [], point: COUNTRIES[key] };
  if (REGIONS[key]) return { kind: "region", label: key, states: REGIONS[key], point: REGION_POINTS[key] };
  const stateKey = STATE_ALIASES[key] || key;
  if (STATES[stateKey]) return { kind: "state", label: stateKey, states: [titleCase(stateKey)], point: STATES[stateKey] };
  const cityKey = CITY_ALIASES[key] || key;
  if (CITIES[cityKey]) {
    const c = CITIES[cityKey];
    return { kind: "city", label: cityKey, states: [titleCase(c.state)], point: { lat: c.lat, lon: c.lon } };
  }
  return null;
}

function lookupCityFirst(key) {
  if (!key) return null;
  const cityKey = CITY_ALIASES[key] || key;
  if (CITIES[cityKey]) {
    const c = CITIES[cityKey];
    return { kind: "city", label: cityKey, states: [titleCase(c.state)], point: { lat: c.lat, lon: c.lon } };
  }
  const stateKey = STATE_ALIASES[key] || key;
  if (STATES[stateKey]) return { kind: "state", label: stateKey, states: [titleCase(stateKey)], point: STATES[stateKey] };
  if (REGIONS[key]) return { kind: "region", label: key, states: REGIONS[key], point: REGION_POINTS[key] };
  if (COUNTRIES[key]) return { kind: "country", label: key, states: [], point: COUNTRIES[key] };
  return null;
}

function titleCase(s) {
  return s.replace(/\b\w/g, (m) => m.toUpperCase());
}

const NO_MATCH = { matched: false, kind: null, label: "", states: [], point: null };

// ---------------------------------------------------------------------------
// Main entry
// ---------------------------------------------------------------------------

export function resolveLocation(raw) {
  const clean = normalize(raw);
  if (!clean) return NO_MATCH;

  // 1. whole label is a known place
  const exact = lookupExact(clean);
  if (exact) return { ...exact, matched: true };

  // 2. whole label is a short code (TL, AP, WB ...)
  if (SHORT_CODES[clean]) {
    const st = SHORT_CODES[clean];
    return { matched: true, kind: "state", label: st, states: [titleCase(st)], point: STATES[st] };
  }

  // 3. strip generic words, retry the remainder as a whole
  const tokens = clean.split(" ").filter((t) => t && !STOPWORDS.has(t));
  if (tokens.length) {
    const joined = tokens.join(" ");
    const joinedHit = lookupExact(joined);
    if (joinedHit) return { ...joinedHit, matched: true };
  }

  // 4. scan bigrams then unigrams (cities win over states/regions here)
  for (let i = 0; i < tokens.length - 1; i++) {
    const hit = lookupCityFirst(tokens[i] + " " + tokens[i + 1]);
    if (hit) return { ...hit, matched: true };
  }
  for (let i = 0; i < tokens.length; i++) {
    const hit = lookupCityFirst(tokens[i]);
    if (hit) return { ...hit, matched: true };
  }

  return NO_MATCH;
}

export default resolveLocation;
